import { useCallback, useEffect, useState, useContext, useRef } from "react";
import "date-fns";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import { forwardRef, JSXElementConstructor, ReactElement } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Slide,
	Grid,
	Select,
	MenuItem,
	TextField,
	InputAdornment
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

import { BV2Beneficiery } from "../../../../../types/BV2Beneficiery.types";
import { Member } from "../../../../../types/employee.types";

import { CustomInput } from "../../../../shared/CustomInput";
import { CustomSelectInput } from "../../../../shared/CustomInput";
import { AuthContext, PaycheckContext, ThemeContext } from "../../../../../contexts";
import { dateTimeZoneConverter, dateTimeZoneConverterForDialog } from "../../../../../utils/commonFunctions/date";
import initCapitalize from "../../../../../utils/commonFunctions/initCapitalize";
import { VEB_BENEFIT_TYPES } from "../../../../../constants/VEBBeneficiaryType";
import { VEBBeneficiaryDialogProps } from "../../../../../types/VEBBeneficiaryDialogProps";
import { formatSSN } from "../../../../../utils/commonFunctions/SSN";
import RELATIONSHIPS from "../../../../../constants/relationships";

import EventIcon from "@mui/icons-material/Event";
import { Validation } from "../../../../../types/validation.types";
import CustomHelperTextComponent from "../../../../shared/customHelperTextComponent/CustomHelperTextComponent";
import prefixCharacterAddition from "../../../../../utils/commonFunctions/prefixCharacterAddition";

import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const Transition: JSXElementConstructor<TransitionProps & { children: ReactElement<any, any> }> | undefined =
	forwardRef(function Transition(props, ref) {
		return <Slide direction="up" ref={ref} {...props} />;
	});

const VEBBeneficiaryDialog = (VEBBeneficiaryDialogProps: VEBBeneficiaryDialogProps): JSX.Element => {
	const {
		dialogProps,
		dependents,
		actionType,
		handleAddPrimaryBeneficiary,
		handleAddContingentBeneficiary,
		handleClose,
		handleEditBeneficieryClick
	} = VEBBeneficiaryDialogProps;
	const { openDialog, title, actions, beneficiary, index } = dialogProps;
	const [type, setType] = useState<"PRIMARY" | "CONTINGENT" | null>(null);
	const [percentage, setPercentage] = useState<number | null>(null);
	// const [selectedDependent, setSelectedDependent] = useState<Member | null>(null);
	const [selectedDependent, setSelectedDependent] = useState<any>({
		first_name: "",
		middle_name: "",
		last_name: "",
		SSN: "",
		relationship: "",
		date_of_birth: ""
	});
	const [percentages, setPercentages] = useState<number[]>([]);
	const { theme } = useContext(ThemeContext);
	const [VEBBeneficiaryDialogForm, setVEBBeneficiaryDialogForm] = useState<any[]>([]);
	const [hasSelectDependent, setHasSelectDependent] = useState<boolean>(false);
	const [pasted, setPasted] = useState(false);
	const [validation, setValidation] = useState<Validation>({
		selectedDependent: {},
		type: null,
		percentage: null,
		status: "invalid"
	});
	const _validation = useRef<Validation>();

	const handleValidation = useCallback(async () => {
		const { first_name, last_name, relationship } = selectedDependent;
		_validation.current = {
			...validation,
			type: null,
			percentage: null,
			selectedDependent: {},

			status: "invalid"
		};
		let flag = true;

		if (first_name.length === 0) {
			_validation.current.selectedDependent["first_name"] = "First Name is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (last_name.length === 0) {
			_validation.current.selectedDependent["last_name"] = "Last Name is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (!relationship || relationship.length === 0) {
			_validation.current.selectedDependent["relationship"] = "Relationship is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (type === null) {
			_validation.current["type"] = "Type is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (percentage === null) {
			_validation.current["percentage"] = "Percentage is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [percentage, selectedDependent, type, validation]);

	const handleDialogValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		if (name === "type") {
			setType(value as "PRIMARY" | "CONTINGENT");
		} else if (name === "percentage") {
			setPercentage(Number(value));
		}
	};

	const handleKeyCheck = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>) => {
			let { value } = event.target as HTMLInputElement;
			const { name } = event.target as HTMLInputElement;
			const eventKey = event.key;

			if (name === "SSN") {
				if (eventKey === "Backspace" || eventKey === "Delete") {
					if (/[-]/.test(value) && value.length === 4) {
						event.preventDefault();
						const index = value.indexOf("-");
						value = value.substring(0, index);
						setSelectedDependent(Object.assign({}, selectedDependent, { [name]: value }));
					}
					if (/[-]/.test(value) && value.length === 7) {
						event.preventDefault();
						const index = value.lastIndexOf("-");
						value = value.substring(0, index);
						setSelectedDependent(Object.assign({}, selectedDependent, { [name]: value }));
					}
				}
			}
		},
		[selectedDependent]
	);

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement | HTMLDivElement>) => {
		const { name } = event.target as HTMLInputElement;
		if (name === "SSN") {
			setPasted(true);
			setTimeout(() => null, 2000);
		}
	};

	const handleSelectValueChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let { value } = event.target;
			const { name } = event.target;

			if (name === "first_name") {
				const regex = /^[a-zA-Z]+$/;
				if (value.match(regex) || value === "") {
					value = value.slice(0, 1).toUpperCase() + value.slice(1);
					setSelectedDependent(Object.assign({}, selectedDependent, { [name]: value }));
				} else if (/\s/g.test(value)) {
					setSelectedDependent(Object.assign({}, selectedDependent, { [name]: value }));
				}
			} else if (name === "SSN") {
				const _ssn = value.replaceAll("-", "");
				// findMemberSSN(_ssn);
				if (!pasted) {
					// // console.log("Not Pasted");
					const _value = Number(value.replaceAll("-", ""));
					if (!isNaN(_value)) {
						const ssn = value;
						if (
							!(
								/^[0-9]{0,3}$/.test(value) ||
								/^[0-9]{3}-[0-9]{0,2}$/.test(value) ||
								/^[0-9]{3}-[0-9]{2}-[0-9]{0,4}$/.test(value)
							)
						) {
							value = value.substring(0, value.length - 1);
						} else if (ssn.length === 6) {
							value = value.substring(0, 7) + "-" + value.substring(7);
						} else if (ssn.length === 3) {
							value = value.substring(0, 3) + "-" + value.substring(3);
						}

						setSelectedDependent(Object.assign({}, selectedDependent, { [name]: value }));
					} else {
						event.target.value = "";
						event.target.value = selectedDependent.SSN;
					}
				} else {
					// // console.log("Pasted");
					setPasted(false);
					if (!isNaN(value as any)) {
						if (value.length > 9) {
							event.target.value = "";
							event.target.value = selectedDependent.SSN;
							alert("Please enter a valid 9 digit SSN!");
						} else {
							// // console.log("Inside Number");
							const ssn = value;

							if (ssn.length === 4) {
								value = value.substring(0, 3) + "-" + value.substring(3);
							} else if (ssn.length === 5) {
								value = value.substring(0, 3) + "-" + value.substring(3);
							} else if (ssn.length >= 6 && ssn.length <= 9) {
								value = value.substring(0, 3) + "-" + value.substring(3, 2) + "-" + value.substring(5);
							}

							setSelectedDependent(Object.assign({}, selectedDependent, { [name]: value }));
						}
					} else {
						// // console.log("Inside alpha-numeric");
						let ssnValue = value.replace(/[^0-9]/g, "").replace(/[^\w\s]/gi, "");
						//// // console.log("replaced string", ssnValue);
						alert("You have pasted alpha-numeric SSN!");

						if (ssnValue.length > 9) {
							event.target.value = "";
							event.target.value = selectedDependent.SSN;
							alert("Please enter a valid SSN!");
						} else {
							const ssn = ssnValue;

							if (ssn.length === 4) {
								ssnValue = ssnValue.substring(0, 3) + "-" + ssnValue.substring(3);
							} else if (ssn.length === 5) {
								ssnValue = ssnValue.substring(0, 3) + "-" + ssnValue.substring(3);
							} else if (ssn.length >= 6 && ssn.length <= 9) {
								ssnValue =
									ssnValue.substring(0, 3) +
									"-" +
									ssnValue.substring(3, 2) +
									"-" +
									ssnValue.substring(5);
							}

							setSelectedDependent(Object.assign({}, selectedDependent, { [name]: ssnValue }));
						}
					}
				}
			} else {
				setSelectedDependent(Object.assign({}, selectedDependent, { [name]: value }));
			}
		},
		[pasted, selectedDependent]
	);

	const handleSelectedDependentDateChange = useCallback(
		(_date: dayjs.Dayjs) => {
			const date = dayjs(_date).format("YYYY-MM-DD");
			setSelectedDependent(Object.assign({}, selectedDependent, { ["date_of_birth"]: date }));
		},
		[selectedDependent]
	);

	const handleSelectedDependentChange = useCallback(
		(event: any) => {
			const { value } = event.target;
			// console.log(value, "dependents", dependents);

			dependents.forEach((dependent: Member) => {
				if (dependent.SSN === value) {
					const _dependent = {
						first_name: dependent.first_name,
						middle_name: dependent.middle_name,
						last_name: dependent.last_name,
						SSN: dependent.SSN,
						relationship: Boolean(dependent.relationship) ? dependent.relationship : dependent.role,
						date_of_birth: dateTimeZoneConverterForDialog(dependent.date_of_birth)
					};
					setHasSelectDependent(true);
					setSelectedDependent(Object.assign({}, _dependent as Member));
				}
			});
		},
		[dependents]
	);

	const handleClearBeneficiaryClick = useCallback(() => {
		setHasSelectDependent(false);
		setSelectedDependent(
			Object.assign({}, selectedDependent, {
				first_name: "",
				middle_name: "",
				last_name: "",
				SSN: "",
				relationship: "",
				date_of_birth: null
			})
		);
		setType(null);
		setPercentage(null);
	}, [selectedDependent]);

	const getPercentage = useCallback(() => {
		const _percentages: number[] = [];
		for (let i = 1; i <= 100; i++) {
			_percentages.push(i);
		}
		setPercentages(Object.assign([], _percentages));
	}, []);

	const handleSaveBeneficiaryClick = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			const validation = await handleValidation();
			// console.log("Validation Status", validation, type);
			if (validation === "valid" && beneficiary && handleEditBeneficieryClick && type) {
				const _selectedDependentPayload = {
					...selectedDependent,
					SSN: selectedDependent.SSN.replaceAll("-", ""),
					type
				};
				handleEditBeneficieryClick(beneficiary, _selectedDependentPayload, index, type, percentage as number);
				handleClose();
				return;
			}
			if (validation === "valid") {
				const _selectedDependentPayload = {
					...selectedDependent,
					SSN: selectedDependent.SSN.replaceAll("-", "")
				};
				// console.log("Selected Dependent Payload", _selectedDependentPayload);

				if (type === "PRIMARY") {
					handleAddPrimaryBeneficiary(_selectedDependentPayload, type, percentage as number);
					handleClose();
				} else if (type === "CONTINGENT") {
					handleAddContingentBeneficiary(_selectedDependentPayload, type, percentage as number);
					handleClose();
				}
			}
		},
		[
			beneficiary,
			handleAddContingentBeneficiary,
			handleAddPrimaryBeneficiary,
			handleClose,
			handleEditBeneficieryClick,
			handleValidation,
			index,
			percentage,
			selectedDependent,
			type
		]
	);

	useEffect(() => {
		getPercentage();
	}, [getPercentage]);

	useEffect(() => {
		if (selectedDependent.date_of_birth === null)
			setSelectedDependent(Object.assign({}, selectedDependent, { ["date_of_birth"]: "" }));
	}, [selectedDependent]);

	useEffect(() => {
		setVEBBeneficiaryDialogForm(
			Object.assign(
				[],
				[
					{
						label: "First Name",
						name: "first_name",
						value: selectedDependent?.first_name,
						onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleSelectValueChange(event),
						isDisabled: hasSelectDependent ? true : false,
						type: "textfield"
					},
					{
						label: "Middle Name",
						name: "middle_name",
						value: selectedDependent?.middle_name,
						onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleSelectValueChange(event),
						isDisabled: hasSelectDependent ? true : false,
						type: "textfield"
					},
					{
						label: "Last Name",
						name: "last_name",
						value: selectedDependent?.last_name,
						onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleSelectValueChange(event),
						isDisabled: hasSelectDependent ? true : false,
						type: "textfield"
					},
					{
						label: "SSN",
						name: "SSN",
						value: selectedDependent?.SSN,
						onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleSelectValueChange(event),
						isDisabled: hasSelectDependent ? true : false,
						type: "textfield"
					},
					{
						label: "Relationship",
						name: "relationship",
						value: selectedDependent?.relationship,
						onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleSelectValueChange(event),
						isDisabled: hasSelectDependent ? true : false,
						type: "textfield"
						// options: RELATIONSHIPS
					},
					{
						label: "Date of Birth",
						name: "date_of_birth",
						value: selectedDependent?.date_of_birth
							? dateTimeZoneConverter(selectedDependent?.date_of_birth as string)
							: selectedDependent?.date_of_birth,
						onChange: (date: dayjs.Dayjs) => handleSelectedDependentDateChange(date),
						isDisabled: hasSelectDependent ? true : false,
						type: "date"
					},
					{
						label: "Type",
						name: "type",
						value: type,
						onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleDialogValueChange(event),
						isDisabled: false,
						type: "select",
						options: VEB_BENEFIT_TYPES as string[]
					},
					{
						label: "Percentage",
						name: "percentage",
						value: percentage,
						onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleDialogValueChange(event),
						isDisabled: false,
						type: "select",
						options: percentages as number[]
					}
				]
			)
		);
	}, [
		handleSelectValueChange,
		handleSelectedDependentDateChange,
		hasSelectDependent,
		percentage,
		percentages,
		selectedDependent,
		type
	]);

	// console.log("Selected Dependent", selectedDependent);
	// // console.log("Validation", validation);

	useEffect(() => {
		if (beneficiary && beneficiary != null) {
			const {
				SSN,
				date_of_birth,
				first_name,
				insured_SSN,
				last_name,
				middle_name,
				percentage,
				relationship,
				type
			} = beneficiary;
			// console.log(beneficiary);
			// const ssn = SSN.substring(0, 3) + "-" + SSN.substring(3, 5) + "-" + SSN.substring(5);
			// const benefitType = VEB_BENEFIT_TYPES.filter((benefit: any) => benefit == type)[0];

			const _dependent = {
				SSN,
				first_name,
				insured_SSN,
				last_name,
				middle_name,
				relationship: Boolean(relationship) ? relationship : "EMPLOYEE"
			};
			const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/;

			setType(type as "PRIMARY" | "CONTINGENT");
			setPercentage(Number(percentage));

			if (dateRegex.test(date_of_birth)) {
				const date = dayjs(date_of_birth).format("YYYY-MM-DD");
				setSelectedDependent(Object.assign({}, _dependent, { ["date_of_birth"]: date }));
			} else {
				setSelectedDependent(Object.assign({}, _dependent));
			}
			setHasSelectDependent(true);
		} else {
			handleClearBeneficiaryClick();
		}
	}, [beneficiary, openDialog]);
	// console.log("index", index);

	return (
		<div className="VEB-Beneficiary-dialog-container" id="VEB-Beneficiary-dialog-container">
			<Dialog
				// style={{ zIndex: 5000 }}
				open={openDialog}
				onClose={handleClose}
				TransitionComponent={Transition}
				keepMounted
				aria-labelledby="alert-dialog-slide-title"
				aria-describedby="alert-dialog-slide-description"
			>
				<DialogTitle id="alert-dialog-slide-title">
					<div className="custom-dialog-title">{title}</div>
				</DialogTitle>
				<DialogContent>
					<Grid className="grid-container" container columnSpacing={1}>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<div className="select-dependent-container" id="select-dependent-container">
								<div className="details-form-label">Select Dependent</div>
								<Select
									input={<CustomSelectInput />}
									style={{ width: "100%" }}
									name="dependent"
									value={selectedDependent.SSN === "" ? null : selectedDependent?.SSN}
									onChange={handleSelectedDependentChange}
									MenuProps={{
										style: { zIndex: 35960 }
									}}
								>
									{dependents?.map((dependent: Member, index: number) => {
										return (
											<MenuItem key={index} value={dependent?.SSN}>
												{dependent.first_name + " " + dependent.last_name}
											</MenuItem>
										);
									})}
								</Select>
							</div>
						</Grid>
					</Grid>
					<div>
						<Grid className="grid-container" container columnSpacing={1}>
							{VEBBeneficiaryDialogForm?.map((field, index) => {
								const { label, value, name, onChange, isDisabled, type, options } = field;
								return (
									<>
										<Grid item xl={4} lg={4} md={4} sm={4} xs={4}>
											<div>{label}</div>
											{type === "textfield" ? (
												<>
													<CustomInput
														key={index}
														name={name}
														value={name === "SSN" && isDisabled ? formatSSN(value) : value}
														style={{ width: "100%" }}
														onChange={onChange}
														disabled={isDisabled}
														onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
															handleKeyCheck(event)
														}
														onPaste={(event: React.ClipboardEvent<HTMLDivElement>) =>
															handlePaste(event)
														}
													/>
													{validation && validation.selectedDependent[name] ? (
														<CustomHelperTextComponent
															errorText={validation.selectedDependent[name]}
														/>
													) : null}
												</>
											) : type === "select" ? (
												<>
													<Select
														input={<CustomSelectInput />}
														style={{ width: "100%", zIndex: "99999" }}
														name={name}
														value={value}
														onChange={onChange}
														MenuProps={{
															style: { zIndex: 35960 }
														}}
														disabled={isDisabled}
													>
														{options.map((option: any) => {
															return (
																<MenuItem key={option} value={option}>
																	{name === "type" || name === "relationship"
																		? initCapitalize(option)
																		: option}
																</MenuItem>
															);
														})}
													</Select>
													{validation && validation.selectedDependent[name] ? (
														<CustomHelperTextComponent
															errorText={validation.selectedDependent[name]}
														/>
													) : name === "type" || name === "percentage" ? (
														<CustomHelperTextComponent errorText={validation[name]} />
													) : null}
												</>
											) : field.type === "date" ? (
												<LocalizationProvider dateAdapter={AdapterDayjs}>
													<DesktopDatePicker
														disableFuture
														value={value}
														onChange={onChange}
														disabled={isDisabled}
														InputProps={{
															disableUnderline: true
														}}
														renderInput={(params: any) => (
															<TextField
																{...params}
																error={false}
																// size="small"
																variant="standard"
																underlineStyle={{ display: "none" }}
																style={{
																	backgroundColor: "#f5f5f5",
																	width: "90%",
																	fontSize: "1rem",
																	fontFamily: "IBM Plex Sans, sans-serif",
																	fontWeight: "400px",
																	borderRadius: "10px",
																	padding: "2.5px 10px"
																}}
															/>
														)}
													/>
												</LocalizationProvider>
											) : null}
										</Grid>
									</>
								);
							})}
						</Grid>
					</div>
				</DialogContent>
				<DialogActions style={{ marginBottom: "0.5rem", marginRight: "0.5rem" }}>
					<Button
						variant="contained"
						style={{
							backgroundColor: "#bdbdbd",
							color: "#424242",
							fontWeight: "bold",
							marginLeft: "0.7rem"
						}}
						onClick={handleClearBeneficiaryClick}
					>
						Clear
					</Button>
					<Button
						variant="contained"
						style={{
							backgroundColor: theme.button.background_color,
							color: theme.button.color,
							fontWeight: "bold",
							marginLeft: "0.7rem"
						}}
						onClick={handleSaveBeneficiaryClick}
						className="save-beneficiary-action-button"
					>
						{beneficiary ? "Edit" : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default VEBBeneficiaryDialog;
